import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormModalBaseComponent, IObjectModal } from '../../form-modal-base/form-modal-base.component';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { BaseService } from '../../../base.service';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EstablishmentService } from '../../../../../pages/application/management/establishment/establishment.service';
import { SnackbarService } from '../../../../services/snackbar.service';

type ActionType = 'archive' | 'end-service' | 'update' | 'delete';

interface ActionCopy {
  verb: string;
  noun: string;
  pastParticiple: string;
  mainTitle: (name: string) => string;
  impossibleTitle: (moduleNameWithArticle: string) => string;
  confirmTitle: string;
  autoDependenciesTitle: string;
  confirmMessage: string;
  changeRequiredMessage: string;
  buttonText: string;
  modifyAndActionButton: string;
  checkMessage: (moduleNameWithArticle: string) => string;
}

const ACTION_COPY: Record<ActionType, ActionCopy> = {
  archive: {
    verb: 'archiver',
    noun: 'archivage',
    pastParticiple: 'archivé',
    mainTitle: (name: string) => `Archiver l'élément "${name}"`,
    impossibleTitle: (module: string) => `Impossible d'archiver ${module}`,
    confirmTitle: "Confirmer l'archivage",
    autoDependenciesTitle: 'Des dépendances vont être modifiées automatiquement',
    confirmMessage: "Super, aucune dépendance n'a été trouvée. Souhaitez-vous confirmer l'archivage ?",
    changeRequiredMessage:
      "Des dépendances sont détectées, il est nécessaire de les changer pour continuer l'archivage.",
    buttonText: 'Archiver et modifier automatiquement les dépendances',
    modifyAndActionButton: 'Modifier et archiver',
    checkMessage: (module: string) => `Avant d'archiver ${module}, il est important de vérifier ses dépendances.`,
  },
  'end-service': {
    verb: 'mettre hors service',
    noun: 'mise hors service',
    pastParticiple: 'mis hors service',
    mainTitle: (name: string) => `Mettre hors service l'élément "${name}"`,
    impossibleTitle: (module: string) => `Impossible de mettre hors service ${module}`,
    confirmTitle: 'Confirmer la mise hors service',
    autoDependenciesTitle: 'Des dépendances vont être retirées automatiquement',
    confirmMessage: "Super, aucune dépendance n'a été trouvée. Souhaitez-vous confirmer la mise hors service ?",
    changeRequiredMessage:
      'Des dépendances sont détectées, il est nécessaire de les retirer pour continuer la mise hors service.',
    buttonText: 'Mettre hors service et retirer les dépendances',
    modifyAndActionButton: 'Retirer et mettre hors service',
    checkMessage: (module: string) =>
      `Avant de mettre hors service ${module}, il est important de vérifier ses dépendances.`,
  },
  update: {
    verb: 'mettre à jour',
    noun: 'mise à jour',
    pastParticiple: 'mis à jour',
    mainTitle: (name: string) => `Mettre à jour l'élément "${name}"`,
    impossibleTitle: (module: string) => `Impossible de mettre à jour ${module}`,
    confirmTitle: 'Confirmer la mise à jour',
    autoDependenciesTitle: 'Des dépendances vont être mises à jour automatiquement',
    confirmMessage: "Super, aucune dépendance n'a été trouvée. Souhaitez-vous confirmer la mise à jour ?",
    changeRequiredMessage:
      'Des dépendances sont détectées, il est nécessaire de les adapter pour continuer la mise à jour.',
    buttonText: 'Mettre à jour et modifier automatiquement les dépendances',
    modifyAndActionButton: 'Modifier et mettre à jour',
    checkMessage: (module: string) => `Avant de mettre à jour ${module}, il est important de vérifier ses dépendances.`,
  },
  delete: {
    verb: 'supprimer',
    noun: 'suppression',
    pastParticiple: 'supprimé',
    mainTitle: (name: string) => `Supprimer l'élément "${name}"`,
    impossibleTitle: (module: string) => `Impossible de supprimer ${module}`,
    confirmTitle: 'Confirmer la suppression',
    autoDependenciesTitle: 'Des dépendances vont être retirées automatiquement',
    confirmMessage: "Super, aucune dépendance n'a été trouvée. Souhaitez-vous confirmer la suppression ?",
    changeRequiredMessage:
      'Des dépendances sont détectées, il est nécessaire de les remplacer pour continuer la suppression.',
    buttonText: 'Supprimer et modifier automatiquement les dépendances',
    modifyAndActionButton: 'Modifier et supprimer',
    checkMessage: (module: string) => `Avant de supprimer ${module}, il est important de vérifier ses dépendances.`,
  },
};

export interface ArchiveDeleteConfig extends IObjectModal {
  isStepByStep: boolean;
  dependencies: ArchiDeleteDependencies;
  callback?: () => Promise<ArchiDeleteDependencies>;
  nameWithArticle?: string;
  titleWithArticle?: string;
  actionType?: ActionType;
}

export interface DependencyData {
  dependencies: [];
  name: string;
}

interface DependenciesByTenant {
  tenant: string;
  categories: DependencyData[];
}

export interface ArchiDeleteDependencies {
  data: DependencyData[];
  hasDependencies: boolean;
  canChangeDependencies?: boolean;
  canArchiveWithDependencies?: boolean;
  mainEstablishments?: { _id: string; label: string; buildings?: { _id: string; label: string }[] }[];
}

export interface ChangeArchiveDeleteData extends IObjectModal {
  service: BaseService<any>;
  id: string;
}

interface FieldConfig {
  controlName: string;
  label: string;
  filters: Record<string, any>;
  emptyMessage: string;
}

@Component({
  selector: 'vex-archive-delete',
  templateUrl: './archive-delete.component.html',
  styleUrls: ['./archive-delete.component.scss'],
})
export class ArchiveDeleteComponent<T> extends FormModalBaseComponent<T> implements OnInit, OnDestroy {
  showingDependencies = false;
  isStepByStep = false;
  dependencies: ArchiDeleteDependencies;
  groupedDependencies: any[] = [];
  hasNotDependencies = false;
  name: string;
  canChangeDependencies: boolean;
  serviceToUse: any;
  items$: Observable<any[]>;
  form: UntypedFormGroup = new UntypedFormGroup({});
  fieldConfig: FieldConfig | null = null;
  noItemsMessage = "Aucun élément n'est disponible pour changer les dépendances";
  canArchiveWithDependencies = false;
  mainEstablishments: { _id: string; label: string; buildings: any[]; selectedBuildingId?: string }[] = [];
  actionType: ActionType = 'archive';

  private configMap: Record<string, FieldConfig> = {
    'metrik-type-holding': {
      controlName: 'detentionType',
      label: 'Type de détention',
      filters: { isActive: true, isArchive: false, isDelete: false },
      emptyMessage: "Aucun type de détention n'est disponible pour changer les dépendances",
    },
    'lease-type': {
      controlName: 'leaseType',
      label: 'Type Bail',
      filters: { isActive: true, isArchive: false, isDelete: false },
      emptyMessage: "Aucun type bail n'est disponible pour changer les dépendances",
    },
    'generic-building-category': {
      controlName: 'genericBuildingCategory',
      label: 'Catégorie de bâtiment générique',
      filters: { isActive: true, isArchive: false, isDelete: false },
      emptyMessage: "Aucune catégorie de bâtiment générique n'est disponible pour changer les dépendances",
    },
    'generic-building': {
      controlName: 'genericBuilding',
      label: 'Bâtiment générique',
      filters: { isActive: true, isDelete: false },
      emptyMessage: "Aucun bâtiment générique n'est disponible pour changer les dépendances",
    },
    'payment-method': {
      controlName: 'paymentMethod',
      label: 'Mode de règlement',
      filters: { isArchive: false, isDelete: false },
      emptyMessage: "Aucun mode de règlement n'est disponible pour changer les dépendances",
    },
    'product-category': {
      controlName: 'productCategory',
      label: "Catégorie d'article",
      filters: { isArchive: false },
      emptyMessage: "Aucune catégorie d'article n'est disponible pour changer les dépendances",
    },
    'prestation-category': {
      controlName: 'prestationCategory',
      label: 'Catégorie de prestation',
      filters: { isArchive: false },
      emptyMessage: "Aucune catégorie de prestation n'est disponible pour changer les dépendances",
    },
  };

  private async processDependencies(dep: ArchiDeleteDependencies) {
    this.dependencies = dep;
    this.groupedDependencies = [];
    this.canArchiveWithDependencies = !!dep?.canArchiveWithDependencies;
    this.mainEstablishments = [];
    if (this.serviceToUse?.entityApi === 'building') {
      this.mainEstablishments = (dep.mainEstablishments || []).map(est => ({
        ...est,
        buildings: est.buildings || [],
      }));
    }
    if (!dep?.data?.length) {
      return;
    }

    const first = dep.data[0];
    if (!first.name.startsWith('Tenant :')) {
      return;
    }

    const tenantMap: Record<string, { [key: string]: string[] }> = {};
    for (const item of dep.data) {
      const match = item.name.match(/^Tenant :\s*(.*?)\s*-\s*(.*)$/);
      if (!match) {
        continue;
      }
      const tenant = match[1];
      const category = match[2];
      if (!tenantMap[tenant]) {
        tenantMap[tenant] = {};
      }
      if (!tenantMap[tenant][category]) {
        tenantMap[tenant][category] = [];
      }
      tenantMap[tenant][category].push(...item.dependencies);
    }

    this.groupedDependencies = Object.entries(tenantMap).map(([tenant, cats]) => ({
      tenant,
      categories: Object.entries(cats).map(([name, dependencies]) => ({ name, dependencies })),
    }));
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ArchiveDeleteConfig & { service: BaseService<T> },
    public dialogRef: MatDialogRef<FormModalBaseComponent<T>>,
    private establishmentService: EstablishmentService,
    snackbarService: SnackbarService,
  ) {
    super(data, dialogRef, data.service);
    this.snackbarService = snackbarService;
  }

  async ngOnInit() {
    super.ngOnInit();
    this.serviceToUse = this.data.service;
    this.fieldConfig = this.configMap[this.serviceToUse.entityApi] || null;
    if (this.fieldConfig) {
      this.noItemsMessage = this.fieldConfig.emptyMessage;
    }
    if (this.fieldConfig) {
      const { controlName } = this.fieldConfig;
      this.form = new UntypedFormGroup({
        [controlName]: new UntypedFormControl(this.defaults?.[controlName], Validators.required),
      });
      this.itemsAutoCompleteChange('');
    } else {
      this.form = new UntypedFormGroup({});
    }
    this.isStepByStep = this.data.isStepByStep;
    this.name = this.data.name;
    this.actionType = this.data.actionType || 'archive';
    if (!this.isStepByStep) {
      this.loading = true;
      const deps = await this.data.callback();
      await this.processDependencies(deps);
      this.canChangeDependencies = this.dependencies.canChangeDependencies;
      this.canArchiveWithDependencies = !!this.dependencies.canArchiveWithDependencies;
      this.showingDependencies = true;
      this.loading = false;
    }
    const resultData = await this.data.callback();
    await this.processDependencies(resultData);
    this.canChangeDependencies = !!resultData.canChangeDependencies;
    this.canArchiveWithDependencies = !!this.dependencies.canArchiveWithDependencies;
  }

  async checkDependencies() {
    this.loading = true;
    const deps = await this.data.callback();
    await this.processDependencies(deps);
    this.canChangeDependencies = !!deps.canChangeDependencies;
    this.canArchiveWithDependencies = !!deps.canArchiveWithDependencies;
    this.loading = false;
    if (this.dependencies.hasDependencies) {
      this.showingDependencies = true;
    } else {
      this.hasNotDependencies = true;
    }
  }

  itemsAutoCompleteChange(term = '') {
    if (!this.fieldConfig) {
      return;
    }

    const { controlName, filters } = this.fieldConfig;

    this.items$ = this.serviceToUse
      .findAll(null, null, null, null, term, filters)
      .pipe(map((res: any) => res.data.filter((item: any) => item._id !== this.data.defaults._id)));
  }

  async assignMainBuilding(est: { _id: string; label: string; buildings: any[]; selectedBuildingId?: string }) {
    if (!est.selectedBuildingId) {
      return;
    }
    this.loading = true;
    try {
      await firstValueFrom(this.establishmentService.updateMainBuilding(est._id, est.selectedBuildingId, true));
      this.snackbarService.success('Vous avez bien réassigné un bâtiment principal');
      const deps = await this.data.callback();
      await this.processDependencies(deps);
      this.canChangeDependencies = !!deps.canChangeDependencies;
      this.canArchiveWithDependencies = !!deps.canArchiveWithDependencies;
      if (deps.hasDependencies) {
        this.showingDependencies = true;
        this.hasNotDependencies = false;
      } else {
        // Plus de dépendances après réassignation
        // Pour end-service : fermer modale et ouvrir modale metrik
        if (this.actionType === 'end-service') {
          this.dialogRef.close('proceed-to-metrik');
        } else {
          // Pour archive : afficher écran de confirmation si isStepByStep
          this.showingDependencies = false;
          this.hasNotDependencies = true;
        }
      }
    } finally {
      this.loading = false;
    }
  }

  changeAndArchiveGeneric() {
    this.loading = true;
    const fnName =
      this.actionType === 'delete'
        ? 'changeAndDeleteGeneric'
        : this.actionType === 'update'
          ? 'changeAndUpdateGeneric'
          : 'changeAndArchiveGeneric';
    const payload =
      this.actionType === 'update'
        ? { ...this.form.getRawValue(), update: this.data.defaults }
        : this.form.getRawValue();
    const sub = this.serviceToUse[fnName](this.data.defaults._id, payload).subscribe(
      result => {
        this.afterArchiveItem(result, null);
        this.loading = false;
      },
      error => {
        this.afterArchiveItem(null, error);
        this.loading = false;
      },
    );
    this.subscription.add(sub);
  }

  executePrimaryAction() {
    if (this.actionType === 'delete') {
      this.loading = true;
      const sub = this.serviceToUse.delete(this.data.defaults).subscribe(
        result => {
          this.afterArchiveItem(result || true, null);
          this.loading = false;
        },
        error => {
          this.afterArchiveItem(null, error);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
      return;
    }

    if (this.actionType === 'update') {
      this.loading = true;
      const sub = this.serviceToUse.update(this.data.defaults).subscribe(
        result => {
          this.afterArchiveItem(result || true, null);
          this.loading = false;
        },
        error => {
          this.afterArchiveItem(null, error);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
      return;
    }

    this.archiveItem();
  }

  getPrimaryActionLabel(): string {
    switch (this.actionType) {
      case 'end-service':
        return 'Mettre hors service';
      case 'delete':
        return 'Supprimer';
      case 'update':
        return 'Mettre à jour';
      default:
        return 'Archiver';
    }
  }

  changeAutoDependenciesAndArchive() {
    // Pour end-service, on ferme la modale avec un code spécial pour ouvrir la modale de metrik
    if (this.actionType === 'end-service') {
      this.dialogRef.close('proceed-to-metrik');
      return;
    }

    // Pour archive ou mise à jour, comportement normal
    this.loading = true;
    const sub = this.serviceToUse.changeAutoDependenciesAndArchive(this.data.defaults._id).subscribe(
      result => {
        this.afterArchiveItem(result, null);
        this.loading = false;
      },
      error => {
        this.afterArchiveItem(null, error);
        this.loading = false;
      },
    );
    this.subscription.add(sub);
  }

  // Override pour garantir qu'on retourne toujours une valeur truthy en cas de succès
  afterArchiveItem(result: any, error: any) {
    if (result) {
      // Retourner result.data s'il existe, sinon true pour garantir le refresh
      this.close(result.data || true);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  // Méthodes d'aide pour les textes dynamiques
  private getActionCopy(): ActionCopy {
    return ACTION_COPY[this.actionType];
  }

  getActionVerb(): string {
    return this.getActionCopy().verb;
  }

  getActionNoun(): string {
    return this.getActionCopy().noun;
  }

  getActionPastParticiple(): string {
    return this.getActionCopy().pastParticiple;
  }

  getMainTitle(): string {
    return this.getActionCopy().mainTitle(this.name);
  }

  getImpossibleTitle(): string {
    const moduleNameWithArticle = this.data.nameWithArticle || "l'élément";
    return this.getActionCopy().impossibleTitle(moduleNameWithArticle);
  }

  getConfirmTitle(): string {
    return this.getActionCopy().confirmTitle;
  }

  getAutoDependenciesTitle(): string {
    return this.getActionCopy().autoDependenciesTitle;
  }

  getCheckMessage(): string {
    const moduleNameWithArticle = this.data.nameWithArticle || this.name;
    return this.getActionCopy().checkMessage(moduleNameWithArticle);
  }

  getConfirmMessage(): string {
    return this.getActionCopy().confirmMessage;
  }

  getChangeRequiredMessage(): string {
    return this.getActionCopy().changeRequiredMessage;
  }

  getButtonText(): string {
    return this.getActionCopy().buttonText;
  }

  getModifyAndActionButton(): string {
    return this.getActionCopy().modifyAndActionButton;
  }

  /**
   * Vérifie si un type de dépendance spécifique existe dans les dépendances
   * @param typeName Le nom du type de dépendance à chercher (ex: 'Tickets', 'Contrats')
   */
  hasDependencyType(typeName: string): boolean {
    if (!this.dependencies?.data) {
      return false;
    }
    return this.dependencies.data.some(
      dep => dep.name.toLowerCase().includes(typeName.toLowerCase()) && dep.dependencies.length > 0,
    );
  }
}
